import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  Button,
  Divider,
  Form,
  Input,
  Modal,
  Popover,
  Select,
  message
} from "antd";
import { useForm } from "antd/lib/form/Form";
import {
  dummyColumnDef,
  IntegrationNameCellRenderer,
  integrationTypeCellRenderer,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { GridLoadingIndicator } from "components/widgets";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { getOAuthUrl } from "services/integration-service";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useIntegrationAuthProcess } from "hooks/integration-hooks";
import SubscriptionIntegrationListMessage from "components/widgets/subscription-integration-list-message";

import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import CiqLogPageHeader, {
  calculateLogPageStats
} from "components/ciq-log-page-header";
import {
  ErrorMessages,
  IntegrationAuthProcessStatuses,
  IntegrationSystemNameMap,
  SidepanelStyles
} from "../../../constants";
import { IntegrationType, getCIQAUthorizerDetails } from "./utils";
import { IntegrationsTabSharedContext } from "./integrations-tab-context";

function IntegrationsList() {
  const { subscriptionRole } = useContext(SubscriptionContext);
  const { dispatch, sharedState } = useContext(IntegrationsTabSharedContext);

  const gridRef = useRef<AgGridReact>(null);
  const [form] = useForm();

  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();

  const [addingIntegration, setAddingIntegration] = useState(false);

  const { initAuthProcess, resetAuthProcess } = useIntegrationAuthProcess();

  const [openAddIntegrationPanel, setOpenAddIntegrationPanel] = useState(false);

  const [endpointFieldsDisabled, setEndpointFieldsDisabled] = useState(true);

  const canAddIntegration =
    subscriptionRole === RoleSubscriptionEnum.subscription_admin;

  const defaultColDef: {} = useMemo<ColDef>(() => {
    return {
      sortable: true,
      autoHeight: true,
      resizable: true,
      flex: 1,
      minWidth: 100
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    return [
      dummyColumnDef,
      {
        headerName: "Integration Name",
        headerTooltip: "Integration Name",
        menuTabs: [],
        cellRenderer: IntegrationNameCellRenderer
      },
      {
        headerName: "Source Application",
        field: "integration.system",
        menuTabs: [],
        filter: false,
        sortable: false,
        cellRenderer: integrationTypeCellRenderer
      },
      {
        headerName: "Account Name",
        menuTabs: [],
        filter: false,
        sortable: false,
        valueGetter: ({ data }: any) => {
          return (
            data.properties.PROCORE_COMPANY_NAME ||
            data.properties.AUTODESK_ACCOUNT_NAME
          );
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "End Point",
        headerTooltip: "End Point",
        menuTabs: [],
        minWidth: 250,
        sortable: false,
        filter: false,
        valueGetter: ({ data }: any) => {
          return (
            data.properties.PROCORE_ROOT_URL || data.properties.ACC_ROOT_URL
          );
        },
        cellRenderer: truncatedTextCellRenderer
      },
      {
        headerName: "Added BY",
        headerTooltip: "Added BY",
        menuTabs: [],
        sortable: false,
        valueGetter: (params: any) => {
          const authorizerDetails = getCIQAUthorizerDetails(
            params.data.properties
          );
          const emailStr = authorizerDetails.email
            ? `(${authorizerDetails.email})`
            : "";
          return `${authorizerDetails.name} ${emailStr}`;
        },
        cellRenderer: truncatedTextCellRenderer
      }
    ];
  }, []);

  const availableIntegrationOptions = useMemo(() => {
    if (!sharedState?.availableIntegrations) return [];

    const activeSubSourceType = ["AUTODESK", "PROCORE"];

    return sharedState?.availableIntegrations
      ?.filter((opt: any) => activeSubSourceType.includes(opt?.system))
      ?.map((availableIntegration: any) => {
        return {
          value: availableIntegration.id,
          label: IntegrationSystemNameMap[availableIntegration.system]
        };
      });
  }, [sharedState?.availableIntegrations]);

  const selectedIntegrationType = Form.useWatch("integrationType", form);

  useEffect(() => {
    if (selectedIntegrationType === IntegrationType.PROCORE) {
      const foundProcoreConfig = sharedState?.systemIntegrationConfigs.find(
        (config: any) => {
          return config.system === IntegrationType[IntegrationType.PROCORE];
        }
      );

      form.setFieldsValue({
        root_url:
          foundProcoreConfig.integration_config_properties.PROCORE_ROOT_URL,
        login_url:
          foundProcoreConfig.integration_config_properties.PROCORE_LOGIN_URL
      });
    }
  }, [form, selectedIntegrationType, sharedState?.systemIntegrationConfigs]);

  const onFormSubmit = async (values: any) => {
    setAddingIntegration(true);
    let payload = {};
    if (values.integrationType === IntegrationType.PROCORE) {
      payload = {
        login_url: values.login_url,
        root_url: values.root_url
      };
    }
    const OAuthURLResponse = await getOAuthUrl(
      IntegrationType[values.integrationType],
      payload
    );
    setAddingIntegration(false);
    if (OAuthURLResponse.success) {
      const authResponse: any = await initAuthProcess(OAuthURLResponse.data);
      // console.log("authResponse ", authResponse);
      if (
        authResponse?.oauth_status?.status ===
        IntegrationAuthProcessStatuses.PROCESSED
      ) {
        setOpenAddIntegrationPanel(false);
        dispatch({
          type: "SET_SHARED_STATE",
          payload: {
            activeView: "configure-initial",
            system: authResponse.source_type.toUpperCase(),
            additionalData: {
              newIntegrationName: form.getFieldValue("integrationName")
            }
          }
        });
      } else {
        const error = authResponse?.output?.error || "Authorization failed";
        message.error(error);
      }
    }
  };

  const onManageIntegrationClick = (data: any) => {
    dispatch({
      type: "SET_SHARED_STATE",
      payload: {
        activeView: "details",
        system: data.integration.system,
        additionalData: data
      }
    });
  };

  const subscriptionIntegrations = useMemo(() => {
    const subIntegrations: any = sharedState?.integrationConfigs?.filter(
      (config: any) =>
        config?.systemIntgConfigProperties?.INTEGRATION_METHOD !== "file"
    );

    return subIntegrations;
  }, [sharedState?.integrationConfigs]);

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  return (
    <div>
      <div>
        <CiqLogPageHeader
          entityName="Integrations"
          titleParam={{
            title: "Integrations",
            totalCount: subscriptionIntegrations?.length || 0,
            filterStats: statsData
          }}
          items={[
            <Button
              type="primary"
              onClick={() => {
                setOpenAddIntegrationPanel(true);
              }}
              disabled={!canAddIntegration || addingIntegration}
              title={
                !canAddIntegration ? ErrorMessages.PermissionNotGranted : ""
              }
              data-testid="add-integration-button"
            >
              Add Integration
            </Button>
          ]}
        />
        <div className="h-[500px] ag-theme-alpine relative">
          {sharedState?.integrationConfigs &&
            !sharedState?.integrationConfigs.length && (
              <SubscriptionIntegrationListMessage
                onConfigureClick={() => {
                  setOpenAddIntegrationPanel(true);
                }}
              />
            )}
          <AgGridReact<any>
            ref={gridRef}
            onFirstDataRendered={() => {
              gridRef?.current?.api?.sizeColumnsToFit();
            }}
            rowData={subscriptionIntegrations}
            columnDefs={columnDefs} // Column Defs for Columns
            defaultColDef={defaultColDef} // Default Column Properties
            animateRows={false} // Optional - set to 'true' to have rows animate when sorted
            loadingOverlayComponent={GridLoadingIndicator}
            suppressDragLeaveHidesColumns
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            tooltipShowMode="whenTruncated"
            onFilterChanged={onFiltersApplied}
            suppressContextMenu
            suppressRowDrag
            suppressMovableColumns
            context={{
              onManageIntegrationClick,
              subscriptionRole
            }}
            suppressNoRowsOverlay
          />
        </div>
      </div>

      <Modal
        className="custom-drawer"
        title="Add Integration"
        width={420}
        style={SidepanelStyles.style}
        bodyStyle={{
          height: "calc(100vh - 57px)",
          padding: "10px 0px 10px 0px"
        }}
        footer={null}
        open={openAddIntegrationPanel}
        onCancel={() => {
          resetAuthProcess();
          setOpenAddIntegrationPanel(false);
        }}
        destroyOnClose
        maskClosable={false}
        keyboard={false}
      >
        <div className="px-3 h-full">
          <Form
            form={form}
            layout="vertical"
            className="h-full"
            onFinish={onFormSubmit}
            disabled={addingIntegration}
          >
            <div className="h-full flex flex-col">
              <div className="grow">
                <div>
                  <Form.Item
                    name="integrationName"
                    label="Integration Name"
                    required
                    rules={[
                      { required: true, message: "Enter Integration Name" }
                    ]}
                  >
                    <Input placeholder="Enter Integration Name" />
                  </Form.Item>
                </div>
                <div>
                  <Form.Item
                    name="integrationType"
                    label="Source Application"
                    required
                    rules={[
                      { required: true, message: "Select source  application" }
                    ]}
                  >
                    <Select
                      options={availableIntegrationOptions}
                      placeholder="Select Source Application"
                    />
                  </Form.Item>
                  {selectedIntegrationType === IntegrationType.AUTODESK && (
                    <div className="pb-2">
                      <div className="mt-2 mb-1 text-xs font-semibold">
                        Install the CIQ App in your Autodesk Account before
                        proceeding with the next steps.
                      </div>
                    </div>
                  )}
                  {selectedIntegrationType === IntegrationType.PROCORE && (
                    <div className="pb-2">
                      <div className="mt-2 mb-1 text-xs font-semibold">
                        Install the CIQ app in your Procore Company before
                        proceeding with the next steps.
                      </div>
                    </div>
                  )}
                </div>
                {selectedIntegrationType === IntegrationType.PROCORE && (
                  <div>
                    <Divider className="mx-0 mt-1 mb-4" />

                    <div>
                      <Form.Item
                        name="root_url"
                        label={
                          <div className="flex items-center">
                            <span>ENDPOINT URL</span>
                            <Popover
                              placement="right"
                              content={
                                <div className="text-sm font-medium max-w-[200px]">
                                  Edit this endpoint if your data is not stored
                                  in the standard Procore zones.
                                </div>
                              }
                            >
                              <InfoCircleOutlined className="px-0.5 -mt-0.5" />
                            </Popover>
                          </div>
                        }
                        required
                        rules={[
                          { required: true, message: "Enter the endpoint URL" }
                        ]}
                      >
                        <Input disabled={endpointFieldsDisabled} />
                      </Form.Item>
                    </div>

                    <div>
                      <Form.Item
                        name="login_url"
                        label={
                          <div className="flex items-center">
                            <span>LOGIN URL</span>
                            <Popover
                              placement="right"
                              content={
                                <div className="text-sm font-medium max-w-[200px]">
                                  Edit this endpoint if your user data is not
                                  stored in the standard Procore zones. <br />{" "}
                                  <a
                                    target="_blank"
                                    href="https://developers.procore.com/reference/rest/v1/docs/tutorial-mpz"
                                    rel="noreferrer"
                                  >
                                    Refer docs
                                  </a>{" "}
                                  for details.
                                </div>
                              }
                            >
                              <InfoCircleOutlined className="px-0.5 -mt-0.5" />
                            </Popover>
                          </div>
                        }
                        required
                        rules={[
                          { required: true, message: "Enter the login URL" }
                        ]}
                      >
                        <Input disabled={endpointFieldsDisabled} />
                      </Form.Item>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        size="middle"
                        onClick={() => {
                          setEndpointFieldsDisabled(!endpointFieldsDisabled);
                        }}
                      >
                        {endpointFieldsDisabled ? "Edit" : "Cancel"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                {selectedIntegrationType === IntegrationType.AUTODESK && (
                  <div className="mt-2 mb-1 text-xs font-semibold">
                    <span className="font-semibold mb-1">
                      The next step is to login to Autodesk.
                    </span>
                    <br />
                    <span className="font-semibold mb-1">
                      To login, use the credentials of an Autodesk Construction
                      Cloud Account Admin who is part of at least one Autodesk®
                      Project.
                    </span>
                    <br />
                    <span className="font-semibold mb-1">
                      Read{" "}
                      <a
                        href="https://apps.autodesk.com/BIM360/en/Detail/HelpDoc?appId=4545831120489722584&appLang=en&os=Web"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Help Document
                      </a>{" "}
                      for more details.
                    </span>
                  </div>
                )}
                {selectedIntegrationType === IntegrationType.PROCORE && (
                  <div className="p-1 text-xs">
                    <span className="font-semibold mb-1">
                      The next step is to login to Procore.
                    </span>
                    <ul className="m-0 pl-4">
                      <li>
                        If you are already logged into Procore in another tab,
                        you will not need to log in again after clicking Next.
                      </li>
                      <li>
                        If you want to use a different credential, please log
                        out of Procore before clicking Next.
                      </li>
                    </ul>
                  </div>
                )}
                <Divider className="mx-0 mt-1 mb-4" />
                <div className="flex justify-end space-x-4">
                  <Button
                    htmlType="reset"
                    className="px-5"
                    onClick={() => {
                      resetAuthProcess();
                      setOpenAddIntegrationPanel(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="px-5"
                    disabled={addingIntegration}
                    loading={addingIntegration}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
}

export default IntegrationsList;
